// src/components/Dealer/DealerLicenseCheck.tsx

import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { type DealerLicenseStatus, dealerApi } from '../../services/dealerApi';

// 딜러 앱 내 정보와 같은 종사원증 상태
const LICENSE_STATUS: Record<DealerLicenseStatus['license_status'], { label: string; className: string }> = {
  verified: { label: '확인됨', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  pending: { label: '확인 중', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  none: { label: '기록 없음', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};

interface DealerLicenseCheckProps {
  license: DealerLicenseStatus;
  /** 확인됨으로 바꾼 뒤 (상태·사진 다시 불러오기) */
  onApproved: () => void;
  onPreview: (url: string) => void;
}

/**
 * 딜러 상세 — 종사원증 확인 상태와, 끝까지 인식되지 않은 번호(확인 중)를 '확인됨'으로 바꾸는 버튼.
 * 거절은 정책을 정한 뒤에 넣는다.
 */
export default function DealerLicenseCheck({ license, onApproved, onPreview }: DealerLicenseCheckProps) {
  const pending = license.pending;
  const status = LICENSE_STATUS[license.license_status] ?? LICENSE_STATUS.none;

  // 교체 요청이면 요청 사진이 지금 사진과 따로 있다 (가입이면 위 '종사원증 사진'이 그 사진)
  const [requestImageUrl, setRequestImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pending || pending.source !== 'replace' || !pending.has_photo) {
      setRequestImageUrl(null);
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    dealerApi.fetchLicenseChangeImageUrl(pending.id).then((url) => {
      if (cancelled) {
        if (url) window.URL.revokeObjectURL(url);
        return;
      }
      created = url;
      setRequestImageUrl(url);
    });

    return () => {
      cancelled = true;
      if (created) window.URL.revokeObjectURL(created);
    };
  }, [pending]);

  const approveMutation = useMutation({
    mutationFn: (changeId: number) => dealerApi.approveLicenseChange(changeId),
    onSuccess: (res) => {
      toast.success(res.message || '확인됨으로 바꿨습니다.');
      onApproved();
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || '처리하지 못했습니다.');
      onApproved(); // 이미 처리된 요청(409)일 수 있으니 상태를 다시 불러온다
    },
  });

  const handleApprove = () => {
    if (!pending) return;
    const message =
      pending.source === 'replace'
        ? `종사원증 번호가 ${pending.old_number ?? '(없음)'} → ${pending.new_number} 로 바뀌고 '확인됨'이 됩니다.\n요청 사진과 번호를 확인하셨나요?`
        : `가입 때 입력한 번호 ${pending.new_number} 를 '확인됨'으로 바꿉니다.\n종사원증 사진과 번호를 확인하셨나요?`;
    if (window.confirm(message)) {
      approveMutation.mutate(pending.id);
    }
  };

  return (
    <div className='mt-6 border-t border-gray-200 pt-4 dark:border-gray-700'>
      <div className='mb-3 flex items-center gap-2'>
        <p className='text-xs text-gray-500 dark:text-gray-400'>종사원증 확인</p>
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
      </div>
      <p className='text-sm text-gray-800 dark:text-white/90'>
        지금 번호 <span className='font-medium'>{license.license_number || '-'}</span>
      </p>

      {pending && (
        <div className='mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-900/10'>
          <p className='text-sm font-medium text-amber-800 dark:text-amber-300'>
            {pending.source === 'replace'
              ? '내 정보에서 종사원증 교체 — 끝까지 인식되지 않아 확인을 기다립니다'
              : '가입 때 종사원증이 끝까지 인식되지 않아 직접 입력한 번호로 가입했습니다'}
          </p>
          <div className='mt-2 flex flex-wrap gap-4'>
            {requestImageUrl && (
              <button
                type='button'
                onClick={() => onPreview(requestImageUrl)}
                className='h-28 w-44 overflow-hidden rounded border border-amber-200 bg-white dark:border-amber-900/50'>
                <img src={requestImageUrl} alt='요청 사진' className='h-full w-full object-contain' />
              </button>
            )}
            <div className='space-y-1 text-xs text-gray-700 dark:text-gray-300'>
              {pending.source === 'replace' && <p>지금 번호 {pending.old_number || '-'}</p>}
              <p>
                {pending.source === 'replace' ? '요청 번호' : '입력한 번호'}{' '}
                <span className='font-medium'>{pending.new_number}</span>
              </p>
              {pending.requested_at && <p>{dayjs(pending.requested_at).format('YYYY.MM.DD HH:mm')} 요청</p>}
              {pending.source === 'signup' && <p>사진은 위 '종사원증 사진'에서 확인하세요.</p>}
            </div>
          </div>
          <button
            type='button'
            onClick={handleApprove}
            disabled={approveMutation.isPending}
            className='mt-3 inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50'>
            <ShieldCheck className='h-3.5 w-3.5' />
            {approveMutation.isPending ? '처리 중…' : '확인됨으로 변경'}
          </button>
        </div>
      )}
    </div>
  );
}
